// Store and forward (docs/10 §5, rung 4) — the quiet workhorse of the whole
// design.
//
// Most blocking is partial or temporary: a carrier, an evening, one domain. A
// report does not have to leave DURING the outage to be useful, so the queue
// survives restarts, is capped by count and bytes with oldest-first eviction,
// and keeps each record's delivery history — a report that finally lands at
// 09:00 still faithfully describes 21:53. The history itself is device-local
// (the intake has no field for it and rejects unknown ones), so "how long it
// spent trying" reaches a human through rung 5 — `renderForCopy` — not through
// the upload.
//
// The queue holds documents that are ALREADY redacted. Nothing here inspects or
// rewrites a body; if a body were unsafe, storing it would already be the leak.

import type { KvStore } from '../../storage/kv.js';
import {
  MAX_DELIVERY_HISTORY,
  renderUpload,
  type DeliveryOutcome,
  type DeliveryVia,
  type QueuedReport,
  type ReportBody,
  type ReportReason,
} from './report.js';
import { coarseMinute } from './redact.js';

export const DIAG_QUEUE_KEY = 'merkury.diag.queue.v1';

/** Caps. A queue that can grow without bound on a blocked device is a bug that
 *  fills the user's storage while they cannot even make a call. */
export const QUEUE_MAX_REPORTS = 16;
export const QUEUE_MAX_BYTES = 192_000;

interface PersistedQueue {
  v: 1;
  reports: QueuedReport[];
}

export interface ReportQueueOptions {
  kv: KvStore;
  key?: string;
  maxReports?: number;
  maxBytes?: number;
}

export class ReportQueue {
  private readonly kv: KvStore;
  private readonly key: string;
  private readonly maxReports: number;
  private readonly maxBytes: number;
  private reports: QueuedReport[] = [];
  private loaded = false;
  /** Reports evicted since the last successful upload — reported, not hidden. */
  private evicted = 0;

  constructor(options: ReportQueueOptions) {
    this.kv = options.kv;
    this.key = options.key ?? DIAG_QUEUE_KEY;
    this.maxReports = options.maxReports ?? QUEUE_MAX_REPORTS;
    this.maxBytes = options.maxBytes ?? QUEUE_MAX_BYTES;
  }

  /** Cold start. Tolerant: a corrupt slot costs the queue, never the boot. */
  async load(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    let raw: unknown;
    try {
      raw = await this.kv.get<unknown>(this.key);
    } catch {
      return;
    }
    const stored = (raw as PersistedQueue | undefined)?.reports;
    if (!Array.isArray(stored)) return;
    // Prepended, not assigned, and every mutator awaits this first: a severe
    // failure can queue a report while KV is still answering, and either losing
    // that report or clobbering the persisted ones with it would be the exact
    // failure store-and-forward exists to prevent.
    this.reports = [...stored.filter(isQueuedReport), ...this.reports];
    this.evict();
  }

  list(): QueuedReport[] {
    return this.reports.slice();
  }

  size(): number {
    return this.reports.length;
  }

  bytes(): number {
    return this.reports.reduce((total, r) => total + renderUpload(r).length, 0);
  }

  /** Reports the caps have thrown away since the last drain. */
  droppedReports(): number {
    return this.evicted;
  }

  /**
   * The oldest report eligible to be sent right now, ignoring `skip`.
   *
   * `skip` is what keeps one report that may not be sent — an unconsented one
   * outside dev mode — from standing in front of one the user explicitly asked
   * to send. Consent is per report (§4), so eligibility is too.
   */
  next(nowMs: number, skip?: ReadonlySet<string>): QueuedReport | null {
    for (const report of this.reports) {
      if (report.nextAtMs <= nowMs && skip?.has(report.id) !== true) return report;
    }
    return null;
  }

  /** Earliest device-clock ms at which anything becomes eligible. */
  earliestAtMs(): number | null {
    let earliest: number | null = null;
    for (const report of this.reports) {
      if (earliest === null || report.nextAtMs < earliest) earliest = report.nextAtMs;
    }
    return earliest;
  }

  async enqueue(input: {
    body: ReportBody;
    atMs: number;
    reason?: ReportReason;
    consented?: boolean;
    nextAtMs?: number;
  }): Promise<QueuedReport> {
    await this.load();
    const report: QueuedReport = {
      id: input.body.reportId,
      body: input.body,
      reason: input.reason ?? 'auto',
      queuedAt: coarseMinute(input.atMs),
      attempts: 0,
      history: [],
      nextAtMs: input.nextAtMs ?? input.atMs,
      consented: input.consented === true,
    };
    this.reports.push(report);
    this.evict();
    await this.save();
    return report;
  }

  /**
   * Record what happened to a delivery attempt. The history is DEVICE-LOCAL:
   * `renderUpload` deliberately leaves it off the wire, because the intake
   * rejects any field it does not know and it has none for delivery history
   * (see report.ts). It rides rung 5 instead — `renderForCopy`, the report a
   * user reads and copies out by hand — which is where "how long it spent
   * trying" (docs/10 §5) actually survives today.
   */
  async noteAttempt(
    id: string,
    attempt: { atMs: number; via: DeliveryVia; outcome: DeliveryOutcome; nextAtMs: number },
  ): Promise<void> {
    await this.load();
    const report = this.reports.find((r) => r.id === id);
    if (!report) return;
    report.attempts += 1;
    report.history.push({
      at: coarseMinute(attempt.atMs),
      via: attempt.via,
      outcome: attempt.outcome,
    });
    if (report.history.length > MAX_DELIVERY_HISTORY) {
      report.history.splice(0, report.history.length - MAX_DELIVERY_HISTORY);
    }
    report.nextAtMs = attempt.nextAtMs;
    await this.save();
  }

  /**
   * Grant a one-off consent to a specific queued report (§4, outside dev mode)
   * and clear any backoff standing in front of it — a user who taps "send"
   * should not wait out a retry schedule they never saw.
   *
   * Deliberately touches neither `attempts` nor `history`: those are the
   * DOCUMENT, and changing the document between the preview and the upload
   * would break the one promise §4 makes about preview.
   */
  async consent(id: string): Promise<boolean> {
    await this.load();
    const report = this.reports.find((r) => r.id === id);
    if (!report) return false;
    report.consented = true;
    report.nextAtMs = 0;
    await this.save();
    return true;
  }

  async remove(id: string): Promise<void> {
    await this.load();
    const before = this.reports.length;
    this.reports = this.reports.filter((r) => r.id !== id);
    if (this.reports.length !== before) await this.save();
  }

  async clear(): Promise<void> {
    this.loaded = true; // nothing to restore over a deliberate wipe
    this.reports = [];
    this.evicted = 0;
    await this.kv.delete(this.key).catch(() => undefined);
  }

  /** Oldest-first, until both caps hold. A consented report is not privileged:
   *  eviction order must not depend on anything a censor could influence. */
  private evict(): void {
    while (this.reports.length > this.maxReports) {
      this.reports.shift();
      this.evicted += 1;
    }
    while (this.reports.length > 1 && this.bytes() > this.maxBytes) {
      this.reports.shift();
      this.evicted += 1;
    }
  }

  private async save(): Promise<void> {
    const payload: PersistedQueue = { v: 1, reports: this.reports };
    await this.kv.put(this.key, payload).catch(() => undefined);
  }
}

function isQueuedReport(value: unknown): value is QueuedReport {
  const r = value as Partial<QueuedReport> | null;
  return (
    r !== null &&
    typeof r === 'object' &&
    typeof r.id === 'string' &&
    typeof r.queuedAt === 'number' &&
    typeof r.attempts === 'number' &&
    typeof r.nextAtMs === 'number' &&
    Array.isArray(r.history) &&
    typeof r.body === 'object' &&
    r.body !== null &&
    Array.isArray((r.body as ReportBody).events)
  );
}
